import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { IconButton } from '@mui/material';
import { EnhancedCard } from 'components/enhanced/card';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import { useIsBottomFromTenantAdmin } from 'shared/permissions/useIsBottomFromTenantAdmin';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { examService, examTagService, macroExamService, productService } from 'shared/services';
import { Option } from 'shared/types/general';
import { ProductToCostDialog } from '../dialogs/ProductToCostDialog';
import { ExamsColumns } from 'features/cost-analysis/exams/exams';

interface ProductExamsProps {
  productId: string | number;
}

export const ProductExams = ({ productId }: ProductExamsProps) => {
  const { t } = useTranslation();

  const datagridRefresh = useRef(null);
  const [mode, setMode] = useState('');
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [macroExamOptions, setMacroExamOptions] = useState<string[]>([]);

  const isTenantAdminBottom = useIsBottomFromTenantAdmin();

  useEffect(() => {
    examTagService
      .getAllBaseInformation()
      .then((tags: Option[]) => {
        if (tags && Array.isArray(tags)) {
          setTagOptions(tags.map((tag) => tag.code));
        } else {
          setTagOptions([]);
        }
      })
      .catch(() => {
        setTagOptions([]);
      });
    macroExamService
      .getAllBaseInformation()
      .then((macroExams: Option[]) => {
        if (macroExams && Array.isArray(macroExams)) {
          setMacroExamOptions(macroExams.map((me) => me.code));
        } else {
          setMacroExamOptions([]);
        }
      })
      .catch(() => {
        setMacroExamOptions([]);
      });
  }, []);

  const reloadExams = () => {
    if (datagridRefresh) datagridRefresh.current();
  };

  const getExams = (limit = 0, offset = 0, params: Record<string, unknown> = {}) => {
    return examService.getAllPaginated(limit, offset, { ...params, product: productId });
  };

  const columns = ExamsColumns(productService, +productId, datagridRefresh, tagOptions);

  return (
    <EnhancedCard
      title="Gestisci Esami e Macro esami"
      subtitle="Gestisci gli esami e i macro-esami associati a questo prodotto inserendo i dettagli e le configurazioni richiesti."
      buttons={
        <>
          {isTenantAdminBottom && (
            <div className="flex flex-row items-center gap-3">
              <IconButton color="primary" onClick={() => setMode('exams')} size="small">
                <span className="text-sm font-semibold text-primary-600">{t('menu.exams')}</span>
                <AddCircleOutlineIcon fontSize="small" />
              </IconButton>
              <IconButton color="primary" onClick={() => setMode('macroexams')} size="small">
                <span className="text-sm font-semibold text-primary-600">
                  {t('menu.macro-exams')}
                </span>
                <AddCircleOutlineIcon fontSize="small" />
              </IconButton>
            </div>
          )}
        </>
      }>
      <EnhancedDataGrid
        getMethod={getExams}
        service={examService}
        columns={columns}
        refresh={datagridRefresh}
        disableEdit={!isTenantAdminBottom}
        disableExport={true}
      />
      <ProductToCostDialog
        productId={+productId}
        mode={mode}
        setMode={setMode}
        callback={reloadExams}
      />
    </EnhancedCard>
  );
};
